import React, { useEffect, useState } from 'react';
import CenteredProgress from '../components/CenteredProgress';
import { useGetUserInformationQuery } from '../store/api/GraphQlQuery';
import { Typography } from '@mui/material';
import './ApplicationStateBootstrap.css';
import { avatarUrl } from '../store/data/AvatarUrl';
import { useDispatch } from 'react-redux';
import UserProfile from '../domain/UserProfile';
import CenteredComponent from '../components/CenteredComponent';
import { useTranslation } from 'react-i18next';
import { getMyInfo } from '../common/utils/getMyInfo';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { Auth } from 'aws-amplify';
import fetchDealTypes from '../routes/deals/fetches/fetchDealTypes';
import fetchDealStages from '../routes/deals/fetches/fetchDealStages';
import fetchDealTypeConfigs from '../routes/deals/fetches/fetchDealTypeConfigs';
import {
  loadDealTypeIdAction,
  loadDealTypesAction,
  loadDealTypesConfigMapAction,
  loadDealTypesStagesMapAction,
} from '../store/reducers/deals';

const ensureImageExists = async (url) => {
  return (await fetch(url)).ok ? url : null;
};

// Routes that don't require getMyInfo to succeed (e.g., for external users like Xactimate team)
const isPublicDealRoute = () => {
  const pathname = window.location.pathname;
  return pathname === '/deal' || pathname.startsWith('/deal/');
};

// Check if user is attempting to access a public deal route (persists across login flow)
const isExternalDealAccess = () => {
  // Check current URL first
  if (isPublicDealRoute()) {
    // Store flag so it persists through the auth flow
    try {
      sessionStorage.setItem('externalDealAccess', 'true');
    } catch (e) {
      // Ignore storage errors
    }
    return true;
  }
  // Check if flag was set during login flow
  try {
    return sessionStorage.getItem('externalDealAccess') === 'true';
  } catch (e) {
    return false;
  }
};

// Clear the external access flag (call when user successfully loads as PAIV user or signs out)
const clearExternalAccessFlag = () => {
  try {
    sessionStorage.removeItem('externalDealAccess');
  } catch (e) {
    // Ignore storage errors
  }
};

// Cognito groups that indicate external/read-only users
const EXTERNAL_COGNITO_GROUPS = ['paiv_admin_ro'];

// Check if a Cognito user is an external user (Xactimate team, etc.)
// Checks for membership in specific Cognito groups like 'paiv_admin_ro'
const checkIfExternalCognitoUser = async () => {
  try {
    const cognitoUser = await Auth.currentAuthenticatedUser();

    // Check Cognito groups - groups are in the signInUserSession
    const groups =
      cognitoUser.signInUserSession?.accessToken?.payload?.['cognito:groups'] ||
      [];

    // Check if user is in any external/read-only group
    const isInExternalGroup = EXTERNAL_COGNITO_GROUPS.some((group) =>
      groups.includes(group)
    );

    if (isInExternalGroup) {
      console.log('User is in external Cognito group:', groups);
      return true;
    }

    return false;
  } catch (error) {
    console.error('Error checking Cognito user:', error);
    return false;
  }
};

export default function ApplicationStateBootstrap(props) {
  // For public deal routes, render immediately without any authentication checks
  // This allows Xactimate team members who are Cognito users but not PAIV users to access deals
  if (isPublicDealRoute()) {
    // Set isLoggedIn to true to hide the "Download the Paiv App" section
    if (props.this?.state?.isLoggedIn === false) {
      props.this.setState({ isLoggedIn: true });
    }
    return props.children;
  }

  return <AuthenticatedBootstrap {...props} />;
}

function AuthenticatedBootstrap(props) {
  const dispatch = useDispatch();
  const { t: translation } = useTranslation('', {
    keyPrefix: 'applicationStateBootstrap',
  });
  const { data, isLoading, isError, refetch } = useGetUserInformationQuery();
  const [isImageFetched, setIsImageFetched] = useState(false);
  const [isMyInfoLoaded, setIsMyInfoLoaded] = useState(false);
  const [isExternalUser, setIsExternalUser] = useState(false);
  const userProfile = new UserProfile(data);
  const { signOut, authStatus } = useAuthenticator((context) => [
    context.signOut,
  ]);

  useEffect(() => {
    // NOTE: Update the isLoggedIn state from Application.js when a user logs in/out so that the styling for the log in doesn't carry over into the rest of the app
    if (authStatus === 'authenticated') {
      props.this.setState({ isLoggedIn: true });
    } else {
      props.this.setState({ isLoggedIn: false });
    }
    refetch();
  }, [authStatus]);

  useEffect(() => {
    // Only call getMyInfo once when component mounts and user is authenticated
    if (authStatus === 'authenticated' && !isMyInfoLoaded && !isExternalUser) {
      // First check if this is an external Cognito user (Xactimate team, etc.)
      checkIfExternalCognitoUser().then(async (isExternal) => {
        if (isExternal) {
          // External user (e.g., Xactimate team in paiv_admin_ro group)
          // Skip getMyInfo entirely and allow access
          console.log('External Cognito user detected - allowing access');
          setIsExternalUser(true);
          setIsMyInfoLoaded(true);

          // If not already on a deal route, redirect them there
          if (!isPublicDealRoute()) {
            console.log('Redirecting external user to deal page');
            window.location.href = '/deal';
          }
          return;
        }

        // Not an external user - proceed with normal flow
        const success = await getMyInfo();
        if (!success) {
          // Check if user is attempting external deal access (uses sessionStorage to persist across auth flow)
          if (isExternalDealAccess()) {
            console.log(
              'External user accessing public deal route - allowing access'
            );
            setIsExternalUser(true);
            setIsMyInfoLoaded(true);
          } else {
            console.log('!!!SIGNOUT!!!!');
            signOut();
          }
        } else {
          // Successful login as PAIV user, clear any external access flag
          clearExternalAccessFlag();
          // Successfull login, populate redux with reusable data
          const [defaultCategoryID, normalizedStageCategories] =
            await fetchDealTypes();
          const dealTypesConfigMap = await fetchDealTypeConfigs(
            Object.values(normalizedStageCategories)
          );

          const dealTypesStagesMap = {};

          // Get the dealStages for each dealTypeId, then put it into the map
          for (const dealTypeId of Object.keys(normalizedStageCategories)) {
            const dealStages = await fetchDealStages(
              dealTypeId,
              true, // showAll
              false, // activeOptiom
              false, // allOption
              null, // minSequence
              true // ignoreNoDealStages
            );
            dealTypesStagesMap[dealTypeId] = dealStages;
          }

          dispatch(loadDealTypeIdAction(defaultCategoryID));
          dispatch(loadDealTypesAction(normalizedStageCategories));
          dispatch(loadDealTypesConfigMapAction(dealTypesConfigMap));
          dispatch(loadDealTypesStagesMapAction(dealTypesStagesMap));

          setIsMyInfoLoaded(true);
        }
      });
    }
  }, [authStatus, isMyInfoLoaded, isExternalUser, signOut, dispatch]);

  // External users can bypass the full bootstrap
  if (isExternalUser) {
    return props.children;
  }

  if (isLoading || !isMyInfoLoaded) {
    return <CenteredProgress />;
  } else if (isError) {
    return (
      <CenteredComponent>
        <Typography>{translation('loadingError')}</Typography>
      </CenteredComponent>
    );
  }

  userProfile
    .loadAvatar()
    .then((url) => ensureImageExists(url))
    .then((url) => dispatch(avatarUrl(url)))
    .then(() => setIsImageFetched(true))
    .catch((error) => console.log(error));

  if (!isImageFetched) {
    return <CenteredProgress />;
  }

  return props.children;
}
